<template>
    <div>
        <!-- Sem usuário: o Muven cria o acesso para a loja própria integrar -->
        <ChannelCredentialsPanelCmp
            v-if="!hasCredentials"
            :channel="channel"
            :diagram-state="creatingNewUser ? 'connecting' : 'idle'"
            idle-icon="user"
            title="Crie o usuário de integração"
            lead="Sua loja própria se conecta ao Muven com um usuário e senha exclusivos. Nós criamos tudo para você."
            :tutorial-link="linkTutorialCredentials"
            tutorial-title="Documentação da API"
            tutorial-description="Como a sua loja usa estas credenciais para falar com o Muven.">

            <ol class="cred-custom-steps">
                <li>
                    <span class="cred-custom-step-marker">1</span>
                    <span><strong>O Muven cria o usuário</strong> de integração desta loja.</span>
                </li>
                <li>
                    <span class="cred-custom-step-marker">2</span>
                    <span><strong>Você recebe usuário e senha</strong> nesta tela, prontos para copiar.</span>
                </li>
                <li>
                    <span class="cred-custom-step-marker">3</span>
                    <span><strong>Quem integra a loja usa esses dados</strong> para acessar a API do Muven.</span>
                </li>
            </ol>

            <div class="cred-custom-warning">
                <fa icon="exclamation-triangle" class="cred-custom-warning-icon" aria-hidden="true" />
                <span>Quem tiver estes dados acessa o seu catálogo e pode até criar pedidos. Compartilhe só com pessoas de confiança.</span>
            </div>

            <label class="cred-custom-consent" :for="`${uid}-consent`">
                <input type="checkbox" :id="`${uid}-consent`" :checked="iaccept" @change="iacceptHandler($event.target.checked)" />
                <span class="cred-custom-consent-box" aria-hidden="true"><fa icon="check" /></span>
                <span>Entendi e vou compartilhar estes dados apenas com pessoas de confiança.</span>
            </label>

            <template v-slot:footer-start>
                <router-link :to="`/canais/${channel.id}/configuracoes`" class="btn btn-outline-secondary">
                    <fa icon="arrow-left" />
                    Voltar
                </router-link>
            </template>

            <template v-slot:footer-end>
                <button type="button"
                    class="btn btn-primary"
                    :disabled="creatingNewUser || !iaccept"
                    @click="createNewUser()">
                    <fa icon="sync-alt" spin v-if="creatingNewUser" />
                    <fa icon="user" v-else />
                    {{creatingNewUser ? 'Criando usuário' : 'Criar usuário'}}
                </button>
            </template>
        </ChannelCredentialsPanelCmp>

        <!-- Com usuário: dados para copiar e gestão do acesso -->
        <ChannelCredentialsPanelCmp
            v-else
            :channel="channel"
            :diagram-state="isBusy ? 'connecting' : 'connected'"
            :title="`Acesso da ${channel.name || 'loja própria'} ao Muven`"
            lead="Entregue estes dados a quem integra a sua loja. A senha fica oculta, use o olho para conferir."
            :tutorial-link="linkTutorialCredentials"
            tutorial-title="Documentação da API"
            tutorial-description="Endpoints, autenticação e exemplos para começar a implementação.">

            <form :id="formId" novalidate @submit.prevent="saveCredentials(credentialsForm)">
                <a id="postman-enviroment-data-target-blank" style="display: none"></a>

                <fieldset class="cred-fields" :disabled="isBusy">
                    <legend class="sr-only">Credenciais {{channel.name}}</legend>

                    <CredentialFieldCmp
                        v-for="(field, key) in credentialsForm"
                        :key="key"
                        :ref="`field-${key}`"
                        :field-key="key"
                        :field="field"
                        :channel-enum="channel.enumChannel"
                        :highlighted="highlightedKey === key"
                        copyable />
                </fieldset>

                <div class="cred-custom-tool">
                    <span class="cred-custom-tool-icon" aria-hidden="true"><fa icon="file-download" /></span>
                    <div class="cred-custom-tool-text">
                        <strong>Ambiente Postman</strong>
                        <span>Arquivo com URL e credenciais já preenchidas para testar a API.</span>
                    </div>
                    <button type="button"
                        class="btn btn-outline-secondary cred-custom-button"
                        :disabled="isBusy"
                        @click="getPostmanEnviromentData()">
                        <fa icon="sync-alt" spin v-if="retrievingPostmanEnviromentData" />
                        <fa icon="cloud-download-alt" v-else />
                        Baixar
                    </button>
                </div>

                <div class="cred-custom-danger">
                    <div class="cred-custom-tool-text">
                        <strong>Gerar nova senha</strong>
                        <span>A senha atual para de funcionar na hora. Quem já integrou precisa receber a nova.</span>
                    </div>
                    <button type="button"
                        class="btn btn-outline-danger cred-custom-button"
                        :disabled="isBusy"
                        @click="askGenerateNewPassword()">
                        <fa icon="sync-alt" spin v-if="generatingNewPassword" />
                        <fa icon="key" v-else />
                        {{generatingNewPassword ? 'Gerando' : 'Gerar nova senha'}}
                    </button>
                </div>
            </form>

            <template v-slot:footer-start>
                <router-link :to="`/canais/${channel.id}/configuracoes`" class="btn btn-outline-secondary">
                    <fa icon="arrow-left" />
                    Voltar
                </router-link>

                <span class="cred-dirty" v-if="isDirty" role="status">
                    <span class="cred-dirty-dot" aria-hidden="true"></span>
                    Alterações não salvas
                </span>
            </template>

            <template v-slot:footer-end>
                <button type="submit"
                    class="btn btn-primary"
                    :form="formId"
                    :disabled="isBusy">
                    <fa icon="sync-alt" spin v-if="savingContent" />
                    <fa icon="check" v-else />
                    {{savingContent ? 'Salvando' : 'Salvar'}}
                </button>
            </template>
        </ChannelCredentialsPanelCmp>

        <!-- Fora do painel: a animação de entrada dele cria um contexto de
             empilhamento e o modal ficaria embaixo do backdrop do Bootstrap. -->
        <ConfirmByTypingModalCmp
            ref="confirmNewPassword"
            :modal-id="`${uid}-confirm-new-password`"
            title="Gerar nova senha"
            confirmation-word="GERAR"
            confirm-label="Gerar nova senha"
            confirm-hint="A troca não pode ser desfeita."
            @confirm="confirmGenerateNewPassword()">
            <p>
                A senha atual de <strong>{{channel.name}}</strong> deixa de funcionar imediatamente.
                A integração da loja para até receber a nova senha.
            </p>
        </ConfirmByTypingModalCmp>
    </div>
</template>

<script>
import { ChannelModel } from '@/channel/models/ChannelModel'
import channelService from '@/channel/services/ChannelService'
import dynamicCredentialsDataParser from '@/channel/utils/DynamicCredentialsDataParser'
import credentialsFormHelper from '@/channel/utils/CredentialsFormHelper'
import ChannelCredentialsPanelCmp from '@/channel/components/ChannelCredentialsPanelCmp.vue'
import CredentialFieldCmp from '@/channel/components/CredentialFieldCmp.vue'
import ConfirmByTypingModalCmp from '@/utils/components/ConfirmByTypingModalCmp'
import CredentialsTestAndSave from '@/channel/mixins/CredentialsTestAndSave'

const HIGHLIGHT_MS = 2000

export default {
    name: 'ChannelCredentialsSimple',
    components: {
        ChannelCredentialsPanelCmp,
        CredentialFieldCmp,
        ConfirmByTypingModalCmp,
    },
    // Só estado de tela (selo, alterações não salvas); canal Custom não tem teste de conexão
    mixins: [
        CredentialsTestAndSave,
    ],
    props: {
        channel: {
            required: true,
            type: ChannelModel,
            default: new ChannelModel(),
        },
        credentials: {
            required: true,
            type: Object,
            default: Object.create(null),
        },
        schema: {
            required: true,
            type: Object,
            default: Object.create(null),
        },
        linkTutorialCredentials: {
            required: true,
            type: String,
            default: '',
        },
    },
    data() {
        return {
            iaccept: false,
            creatingNewUser: false,
            savingContent: false,
            generatingNewPassword: false,
            retrievingPostmanEnviromentData: false,
            credentialsForm: {},
            highlightedKey: '',
        }
    },
    methods: {
        async createNewUser() {
            this.$floatingAlert.hide()

            this.creatingNewUser = true

            const channelId = this.$route.params.id

            try {
                const user = await channelService.createOrUpdateCustomChannelUser(channelId)
                this.credentialsForm = dynamicCredentialsDataParser.mergeCredentialsSchemaAndServerInformation(this.schema, user)
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.creatingNewUser = false
        },
        async saveCredentials(credentialsForm) {
            this.$floatingAlert.hide()

            this.savingContent = true

            const channelId = this.$route.params.id
            const credentials = dynamicCredentialsDataParser.mountCredentialsObjectFromForm(credentialsForm)

            try {
                const newCredentials = await channelService.saveCredentials(channelId, credentials)

                this.$floatingAlert.success({
                    message: 'Credenciais salvas com sucesso!',
                })

                this.markSaved()
                this.$router.push(`/canais/${channelId}/configuracoes`)
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.savingContent = false
        },
        async generateNewPassword() {
            this.$floatingAlert.hide()

            this.generatingNewPassword = true

            const channelId = this.$route.params.id

            try {
                const updatedUser = await channelService.createOrUpdateCustomChannelUser(channelId)
                this.credentialsForm = dynamicCredentialsDataParser.mergeCredentialsSchemaAndServerInformation(this.schema, updatedUser)
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.generatingNewPassword = false
        },
        async getPostmanEnviromentData() {
            this.$floatingAlert.hide()

            this.retrievingPostmanEnviromentData = true

            const channelId = this.$route.params.id

            try {
                const envJson = await channelService.getPostmanEnviromentData(channelId)
                const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(envJson));
                const downloadElement = document.getElementById('postman-enviroment-data-target-blank');
                downloadElement.setAttribute("href", dataStr);
                downloadElement.setAttribute("download", "postman-enviroment.json");
                downloadElement.click();
            } catch (e) {
                this.$floatingAlert.error({
                    message: e.message,
                })
            }

            this.retrievingPostmanEnviromentData = false
        },
        iacceptHandler(info) {
            this.iaccept = info
        },
        // Trocar a senha derruba quem já integrou: só depois de digitar a confirmação
        askGenerateNewPassword() {
            this.$refs.confirmNewPassword.show()
        },
        async confirmGenerateNewPassword() {
            await this.generateNewPassword()

            // Mostra onde está a senha nova
            const secretKey = Object.keys(this.credentialsForm).find((key) => credentialsFormHelper.isSecretField(key))

            if (secretKey) {
                this.highlightedKey = secretKey
                this.$nextTick(() => this.focusField(secretKey))
                setTimeout(() => {
                    this.highlightedKey = ''
                }, HIGHLIGHT_MS)
            }
        },
    },
    mounted() {
        this.credentialsForm = dynamicCredentialsDataParser.mergeCredentialsSchemaAndServerInformation(this.schema, this.credentials)
    },
    computed: {
        uid() {
            return `cred-custom-${this._uid}`
        },
        formId() {
            return `${this.uid}-form`
        },
        hasCredentials() {
            const formKeys = Object.keys(this.credentialsForm)

            if (!formKeys.length) {
                return false
            }

            for (const key of formKeys) {
                if (!!this.credentialsForm[key].value) {
                    return true
                }
            }

            return false
        },
        isBusy() {
            return this.savingContent || this.generatingNewPassword || this.retrievingPostmanEnviromentData
        },
        isConfigured() {
            return this.hasCredentials
        },
        credentialsStatus() {
            return this.hasCredentials ? 'configured' : 'unconfigured'
        },
    },
}
</script>

<style lang="scss" scoped>
.cred-fields {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
}

/* ── Sem usuário ── */

.cred-custom-steps {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
        display: flex;
        align-items: flex-start;
        gap: 14px;
        padding: 10px 14px;
        color: var(--text-muted);
    }

    strong {
        color: var(--text-strong);
    }
}

.cred-custom-step-marker {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 1px solid var(--border);
    border-radius: 50%;
    background: var(--surface-2);
    color: var(--text-muted);
    font-size: 13px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.cred-custom-warning {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 18px;
    padding: 12px 14px;
    border: 1px solid var(--warning-border);
    border-radius: 10px;
    background: var(--warning-bg);
    color: var(--warning-fg);
    font-size: 14px;
    line-height: 1.45;
}

.cred-custom-warning-icon {
    flex-shrink: 0;
    margin-top: 3px;
}

.cred-custom-consent {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 18px 0 0;
    color: var(--text-strong);
    font-weight: 600;
    cursor: pointer;

    input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
    }
}

.cred-custom-consent-box {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-top: 1px;
    border: 1.5px solid var(--border-strong);
    border-radius: 6px;
    background: var(--surface-1);
    color: transparent;
    font-size: 11px;
    transition: background-color .15s, border-color .15s, color .15s;

    input:checked + & {
        border-color: var(--brand-fill);
        background: var(--brand-fill);
        color: var(--text-on-brand);
    }

    input:focus-visible + & {
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-soft) 30%, transparent);
    }
}

/* ── Com usuário: ferramentas e zona de risco ── */

.cred-custom-tool,
.cred-custom-danger {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
}

.cred-custom-tool {
    margin-top: 28px;
    padding: 14px 16px;
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    background: var(--surface-2);
}

.cred-custom-tool-icon {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: var(--surface-1);
    color: var(--text-soft);
}

.cred-custom-tool-text {
    display: flex;
    flex: 1 1 240px;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    strong {
        color: var(--text-strong);
    }

    span {
        color: var(--text-muted);
        font-size: 14px;
    }
}

.cred-custom-danger {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--border-subtle);
}

.cred-custom-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.cred-dirty {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--warning-fg);
    font-size: 13px;
    font-weight: 700;
}

.cred-dirty-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--warning-solid);
}
</style>
